import Papa from 'papaparse';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import tasksListQuery from '../../api-v2/queries/tasks-list-query';
import { useTenantContext } from '../../context/TenantContext';
import JFTableActions from '../../component-lib/JFTableActions/JFTableActions';
import { useRef, useState } from 'react';
import { TableContainer } from '@mui/material';
import JFTablePagination from '../../component-lib/JFTable/JFTablePagination';
import { Outlet, useLoaderData, useSearchParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import StatHeader from '../../component-lib/StatHeader/StatHeader';
import userListQuery from '../../api-v2/queries/user-list-query';
import { flowsListQuery } from '../../api-v2/queries/flows-list-query';

import { useDocumentTitle } from '../../hooks/use-document-title';
import TasksTable from './TasksTable';
import { getCrmPipelines } from '../../api-v2/queries/crm-queries';

export function getTaskStatus(task) {
  const today = new Date().toISOString().split('T')[0];

  if (task.completed) {
    return 'completed';
  }

  if (task.startDate > today) {
    return 'scheduled';
  }
  if (task.startDate <= today && task.endDate >= today) {
    return 'ongoing';
  }
  if (task.endDate < today) {
    return 'late';
  }
}

export default function TasksPage({ forCurrentUser }) {
  useDocumentTitle('Jointflows', forCurrentUser ? 'My tasks' : 'Queues');
  const queryClient = useQueryClient();
  const { page, pageSize, user, sort, filters } = useLoaderData();
  const { departments_list, crm_system, activate_multi_pipeline } = useTenantContext();
  const [searchText, setSearchText] = useState(filters.search || '');
  const [field, order] = sort.split(':');
  const [orderedBy, setOrderedBy] = useState(field);
  const [orderDirection, setOrderDirection] = useState(order);
  const { data, status, isFetching, refetch } = useQuery(
    tasksListQuery({ page, pageSize, user, sort, filters }),
  );
  const { data: pipelines } = useQuery(
    getCrmPipelines({
      crm: crm_system,
    }),
  );
  const { data: usersData, status: usersQueryStatus } = useQuery(userListQuery());
  const { data: flowsData, status: flowsQueryStatus } = useQuery(flowsListQuery());
  const [searchParams, setSearchParams] = useSearchParams();
  const [exportInProgress, setExportInProgress] = useState(false);

  const tableFilters = [
    {
      id: 'department',
      placeholder: 'Select department',
      value: filters.department || null,
      options: departments_list
        .sort()
        .map((department) => ({ value: department, label: department })),
      onSelectionChange: (value) => {
        searchParams.set('department', value);
        setSearchParams(searchParams);
      },
      onClearClick: () => {
        searchParams.delete('department');
        setSearchParams(searchParams);
      },
    },
    {
      id: 'flow',
      placeholder: 'Select task type',
      value: filters.flow || null,
      loading: flowsQueryStatus === 'pending',
      options: (flowsData || [])
        .filter((flow) => departments_list.includes(flow.department))
        .map((flow) => ({
          value: `${flow.id}`,
          label: flow.label,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      onSelectionChange: (value) => {
        searchParams.set('flow', value);
        setSearchParams(searchParams);
      },
      onClearClick: () => {
        searchParams.delete('flow');
        setSearchParams(searchParams);
      },
    },
    {
      id: 'status',
      placeholder: 'Select status',
      value: filters.status || null,
      options: [
        { value: 'late', label: 'Late' },
        { value: 'ongoing', label: 'Ongoing' },
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'completed', label: 'Completed' },
        { value: 'due_today', label: 'Due today' },
        { value: 'due_tomorrow', label: 'Due tomorrow' },
        { value: 'due_overdue', label: 'Late and due soon' },
      ],
      onSelectionChange: (value) => {
        searchParams.set('status', value);
        setSearchParams(searchParams);
      },
      onClearClick: () => {
        searchParams.delete('status');
        setSearchParams(searchParams);
      },
    },
    {
      id: 'side',
      placeholder: 'Select task side',
      value: filters.side || null,
      options: [
        { value: 'internal', label: 'Internal' },
        { value: 'external', label: 'External' },
        { value: 'shared', label: 'Shared' },
      ],
      onSelectionChange: (value) => {
        searchParams.set('side', value);
        setSearchParams(searchParams);
      },
      onClearClick: () => {
        searchParams.delete('side');
        setSearchParams(searchParams);
      },
    },
    {
      id: 'projectStatus',
      placeholder: 'Select project status',
      value: filters.projectStatus || null,
      options: [
        { value: 'closed', label: 'Closed' },
        { value: 'on track', label: 'On track' },
        { value: 'slipping', label: 'Slipping' },
        { value: 'completed', label: 'Upside' },
      ],
      onSelectionChange: (value) => {
        searchParams.set('projectStatus', value);
        setSearchParams(searchParams);
      },
      onClearClick: () => {
        searchParams.delete('projectStatus');
        setSearchParams(searchParams);
      },
    },
  ];

  if (!forCurrentUser) {
    tableFilters.push({
      id: 'user',
      placeholder: 'Select user',
      value: filters.user || null,
      loading: usersQueryStatus === 'pending',
      options: [
        { label: '<Unassigned>', value: 'unassigned' },
        ...(usersData || [])
          .map((user) => ({
            value: `${user.id}`,
            label: `${user.firstName} ${user.lastName}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ],
      onSelectionChange: (value) => {
        searchParams.set('user', value);
        setSearchParams(searchParams);
      },
      onClearClick: () => {
        searchParams.delete('user');
        setSearchParams(searchParams);
      },
    });
  }

  if (crm_system === 'hubspot' && activate_multi_pipeline) {
    tableFilters.push({
      id: 'pipeline',
      placeholder: 'Select pipeline',
      value: filters.pipeline || null,
      options: (pipelines || []).map((pipeline) => ({
        value: pipeline.id.toString(),
        label: pipeline.name,
      })),
      onSelectionChange: (value) => {
        searchParams.set('pipeline', value);
        setSearchParams(searchParams);
      },
      onClearClick: () => {
        searchParams.delete('pipeline');
        setSearchParams(searchParams);
      },
    });
  }

  const debounceTimeout = useRef(null);

  const handleSearchTextChange = (query) => {
    setSearchText(query);

    if (query.length >= 3 || query.length === 0) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        searchParams.set('search', query);
        setSearchParams(searchParams);
      }, 300);
    }
  };

  const onHeaderCellClick = (columnId) => {
    const newOrderDirection =
      orderedBy === columnId ? (orderDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    setOrderDirection(newOrderDirection);
    setOrderedBy(columnId);

    searchParams.set('sort', `${columnId}:${newOrderDirection}`);
    setSearchParams(searchParams);
  };

  const onPageChange = (event, newPage) => {
    searchParams.set('page', newPage);
    setSearchParams(searchParams);
  };

  const onRowsPerPageChange = (event) => {
    searchParams.set('pageSize', event.target.value);
    searchParams.set('page', 1);
    setSearchParams(searchParams);
  };

  const onExportClick = async () => {
    setExportInProgress(true);
    try {
      const data = await queryClient.fetchQuery(
        tasksListQuery({ page, pageSize: -1, sort, filters }),
      );
      const csv = Papa.unparse(
        data.results.map((task) => ({
          'Project name': task.project.name,
          'Task name': task.title,
          'Task type': task.flow.label,
          'Start date': task.startDate,
          'Planned end date': task.endDate,
          'Actual completion date': task.completionDate,
          'Account name': task.project.buyerCompany.name,
          'Internal assignee': task.internalAssignee
            ? `${task.internalAssignee.firstName} ${task.internalAssignee.lastName}`
            : 'Unassigned',
          'External assignee': task.externalAssignee
            ? `${task.externalAssignee.firstName} ${task.externalAssignee.lastName}`
            : 'Unassigned',
          'Duration (In days)': task.duration,
          // 'Lapse days since start': undefined, // TODO
          'Task side': task.side,
          Status: task.status,
        })),
      );
      const blob = encodeURI('data:text/csv;charset=utf-8,' + csv);

      // Create a virtual Anchor tag
      const link = document.createElement('a');
      link.setAttribute('href', blob);
      link.setAttribute(
        'download',
        `tasks${new Date().toISOString().replace(/[T:-]/g, '_').substring(0, 19)}.csv`,
      );

      // Append the Anchor tag in the actual web page or application
      document.body.appendChild(link);

      // Trigger the click event of the Anchor link
      link.click();

      // Remove the Anchor link form the web page or application
      document.body.removeChild(link);
    } catch (_error) {
      // {}
    } finally {
      setExportInProgress(false);
    }
  };

  if (status === 'pending') {
    return (
      <div
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }}
        className="flex items-center justify-center bg-white bg-opacity-90"
      >
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* <h1 className="text-3xl font-bold mb-10 uppercase text-black">
        {forCurrentUser ?  'My tasks' : 'Queues'}
      </h1> */}
      <StatHeader
        stats={[
          {
            name: 'Total tasks',
            value: data.count,
          },
          {
            name: 'Late',
            value: data.totalLate,
            onClick: () => {
              searchParams.set('status', 'late');
              setSearchParams(searchParams);
            },
          },
          {
            name: 'Ongoing',
            value: data.totalOngoing,
            onClick: () => {
              searchParams.set('status', 'ongoing');
              setSearchParams(searchParams);
            },
          },
          {
            name: 'Upcoming',
            value: data.totalScheduled,
            onClick: () => {
              searchParams.set('status', 'scheduled');
              setSearchParams(searchParams);
            },
          },
        ]}
      />
      <div
        className="flex-1 overflow-hidden"
        style={{ maxHeight: 'calc(100vh - 224px)', position: 'relative' }}
      >
        <JFTableActions
          searchPlaceholder="Search task"
          searchText={searchText}
          handleSearchTextChange={(event) => handleSearchTextChange(event.target.value)}
          handleCancelSearch={() => handleSearchTextChange('')}
          filters={tableFilters}
          onExportClick={onExportClick}
          exportInProgress={exportInProgress}
        />
        <TableContainer style={{ height: '100%', maxHeight: 'calc(100vh - 405px)' }}>
          <TasksTable
            forCurrentUser={forCurrentUser}
            data={data?.results}
            onHeaderCellClick={onHeaderCellClick}
            orderedBy={orderedBy}
            orderDirection={orderDirection}
            refetchData={refetch}
          />
        </TableContainer>
        <JFTablePagination
          totalCount={data.count}
          pageSize={data.pageSize}
          page={data.page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          showTotalCount
        />
        {isFetching && (
          <div
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }}
            className="flex items-center justify-center bg-white bg-opacity-90"
          >
            <Loader />
          </div>
        )}
      </div>
      <Outlet />
    </div>
  );
}
